import { useEffect, useState } from "react";
import { useAppNavigate } from "../lib/route-primitives";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, X } from "lucide-react";
import { SlideshowTitleCard, PresenceFacepile } from "@wedding/slideshow-ui";
import { useWedding } from "../contexts/wedding-context";
import { paths } from "../lib/paths";
import { useAdminSahneWs } from "../hooks/use-admin-sahne-ws";

/**
 * Admin "Sahne'yi İzle" — a WATCH-ONLY live monitor of a wedding's Sahne. No
 * transport / Flow / controls (organizer runs the show). Admin is HIDDEN by
 * default (observer); the "Görünür ol" toggle reconnects visibly so the admin
 * appears in the audience presence with a shield badge and counts in the total.
 */
export function SahnePage() {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const { wedding, slug } = useWedding();
  const [visible, setVisible] = useState(false);
  const { slideState, presence, connected } = useAdminSahneWs(slug, visible);

  const title = wedding?.title ?? slug;
  const media = slideState?.media ?? null;
  const isCard = slideState?.slideType === "card";
  const hasShow = (slideState?.total ?? 0) > 0;

  // Hold the last media url so the title-card cover-bleed matches the guest Sahne.
  const [lastMediaUrl, setLastMediaUrl] = useState<string | null>(null);
  useEffect(() => {
    if (media?.url) setLastMediaUrl(media.url);
  }, [media?.url]);

  return (
    <div className="dark fixed inset-0 z-[80] bg-[#0b0a09] text-white overflow-hidden">
      {/* Stage */}
      <div className="absolute inset-0">
        {!hasShow ? (
          <div className="absolute inset-0 flex items-center justify-center text-white/45 text-sm">
            {connected ? t("slideshowRemote.offline") : t("slideshow.connecting")}
          </div>
        ) : isCard || !media ? (
          <SlideshowTitleCard title={title} coverUrl={lastMediaUrl} />
        ) : media.kind === "image" ? (
          <img key={media.id} src={media.url} alt="" className="w-full h-full object-contain" />
        ) : (
          <video
            key={media.id}
            src={media.url}
            className="w-full h-full object-contain"
            muted
            playsInline
            autoPlay
          />
        )}
      </div>

      {/* Top bar: presence facepile + visibility toggle + exit */}
      <div className="absolute top-[3vmin] right-[3vmin] z-50 flex items-center gap-2">
        <PresenceFacepile presence={presence} />
        <button
          onClick={() => setVisible((v) => !v)}
          className={
            "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold backdrop-blur-md transition-colors cursor-pointer " +
            (visible
              ? "bg-sky-400/15 border-sky-300/40 text-sky-200 hover:bg-sky-400/25"
              : "bg-white/10 border-white/15 text-white/75 hover:bg-white/20")
          }
          title={visible ? t("admin.watchHidden") : t("admin.beVisible")}
        >
          {/* Label + icon show the ACTION the click performs (not current state). */}
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          {visible ? t("admin.watchHidden") : t("admin.beVisible")}
        </button>
        <button
          onClick={() => navigate(paths.wedding.dashboard(slug))}
          aria-label={t("slideshow.exit")}
          className="flex items-center justify-center size-9 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-white hover:bg-rose-500/25 transition-colors cursor-pointer"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Watch-only hint (bottom) */}
      <div className="absolute bottom-[3vmin] left-1/2 -translate-x-1/2 z-40 pointer-events-none">
        <span className="flex items-center gap-1.5 rounded-full bg-black/40 backdrop-blur-md border border-white/10 px-3 py-1 text-[11px] uppercase tracking-widest text-white/55">
          <Eye className="h-3 w-3" /> {t("admin.watchOnly")}
        </span>
      </div>
    </div>
  );
}
